import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { connect } from 'react-redux'
import LoaderSmall from '../../../../uiKit/loaders/loaderSmall'
import NoChartData from '../../../../uiKit/NoChartData'
import Pagination from '../../../../uiKit/table/Pagination'
import { getEmailConfigActivity } from '../../api/emailAutomation'

const TableWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 60px;
  border: 1px solid rgba(235, 235, 235, 1);
`

const ActivityTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #333333;
  margin-bottom: 4px;
  margin-top: 24px;
`

const ActivitySubtitle = styled.div`
  font-size: 14px;
  color: #7A7A7A;
  font-weight: 400;
  margin-bottom: 12px;
`

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1.2fr 3fr 1.8fr 2fr 1fr;
  align-items: center;
  height: 50px;
  padding: 0 20px;
  border-bottom: 1px solid var(--color-issabeline-grey);
  background: var(--color-selago-grey);
  position: sticky;
  top: 0;
  z-index: 1;
`

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  color: #1e1e1e;
  line-height: 21px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BodyRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1.2fr 3fr 1.8fr 2fr 1fr;
  align-items: center;
  min-height: auto;
  padding: 0 20px;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: rgba(97, 101, 129, 1);
  background: ${props => ((props.index + 1) % 2 === 0 ? 'var(--color-table-row-primary)' : 'transparent')};
`

const BodyCell = styled.div`
  padding: 8px 8px 8px 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FileNameCell = styled(BodyCell)`
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
`

const DateTimeCell = styled(BodyCell)`
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
`

const ReasonCell = styled(BodyCell)`
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
`

const StatusText = styled.span`
  font-weight: 600;
  text-transform: capitalize;
  color: ${props => (props.status === 'SUCCESS' ? 'rgba(72, 155, 0, 1)' : 'rgba(255, 41, 41, 1)')};
`

const columnTitles = [
  { title: 'First name' },
  { title: 'Last name' },
  { title: 'File name' },
  { title: 'Date & time' },
  { title: 'Reason' },
  { title: 'Status' },
]

const EmailLogsTable = ({ activeBot }) => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    if (!activeBot || !activeBot.id) return

    const fetchActivity = () => {
      setLoading(true)
      // API is 1-based for page, UI is 0-based
      getEmailConfigActivity(activeBot.id, page + 1, rowsPerPage)
        .then(response => {
          setRows(response?.items || [])
          setTotalCount(response?.total || 0)
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('Error fetching email automation activity:', error)
          setRows([])
          setTotalCount(0)
        })
        .finally(() => {
          setLoading(false)
        })
    }

    fetchActivity()
  }, [activeBot, page, rowsPerPage])

  const formatDateTime = value => {
    if (!value) return ''

    // API provides UTC timestamps. If the backend string has no timezone designator,
    // force UTC interpretation by appending 'Z'.
    const rawValue = value
    const trimmed = typeof rawValue === 'string' ? rawValue.trim() : rawValue
    const hasTimezoneInfo = typeof trimmed === 'string' && /([zZ]|[+-]\d\d:?\d\d)$/.test(trimmed)

    const normalizedUtcString =
      typeof trimmed === 'string' && !hasTimezoneInfo
        ? `${trimmed.replace(' ', 'T')}Z`
        : trimmed

    const date = new Date(normalizedUtcString)

    if (Number.isNaN(date.getTime())) {
      return value
    }

    // Convert to local time and format like: 25/09/2025, 12:00 PM
    const formatted = date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })

    return formatted.replace(/\b(am|pm)\b/i, match => match.toUpperCase())
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    const newRowsPerPage = event.target.value === 'All' ? totalCount : parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  if (loading) {
    return (
      <TableWrapper>
        <div
          style={{
            minHeight: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <LoaderSmall showLoader />
        </div>
      </TableWrapper>
    )
  }

  if (!loading && rows.length === 0) {
    return (
      <>
        <ActivityTitle>Activity log</ActivityTitle>
        <ActivitySubtitle>Last 50 activities are shown in this table</ActivitySubtitle>
        <TableWrapper>
          <NoChartData isUserData={false} text="No email automation activity logs found." />
        </TableWrapper>
      </>
    )
  }

  return (
    <>
      <ActivityTitle>Activity log</ActivityTitle>
      <ActivitySubtitle>Last 50 activities are shown in this table</ActivitySubtitle>
      <TableWrapper>
        <HeaderRow>
          {columnTitles.map(col => (
            <HeaderCell key={col.title}>
              {col.title}
            </HeaderCell>
          ))}
        </HeaderRow>

        {rows.map((row, index) => (
          <BodyRow key={`${row.firstName}-${row.lastName}-${row.fileName}-${index}`} index={index}>
            <BodyCell>{row.firstName}</BodyCell>
            <BodyCell>{row.lastName}</BodyCell>
            <FileNameCell>{row.fileName}</FileNameCell>
            <DateTimeCell>{formatDateTime(row.createdAt)}</DateTimeCell>
            <ReasonCell>{row.reason ? row.reason : '-'}</ReasonCell>
            <BodyCell>
              <StatusText status={row.status}>{(row.status || '').toLowerCase()}</StatusText>
            </BodyCell>
          </BodyRow>
        ))}
        {rows.length > 0 && (
          <div style={{ margin: '20px 0 0 -25px', display: 'flex' }}>
            <Pagination
              count={totalCount}
              rowsPerPage={rowsPerPage === totalCount ? 'All' : rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              hasSelect={true}
              labelRowsPerPage="Rows per page:"
            />
          </div>
        )}
      </TableWrapper>
    </>
  )
}

const mapStateToProps = state => ({
  activeBot: state.activeBot,
})

EmailLogsTable.propTypes = {
  activeBot: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
}

export default connect(mapStateToProps)(EmailLogsTable)